import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Tooltip,
  Modal,
  Checkbox,
  Tag,
  Tabs
} from 'antd';
import {
  StatusPill,
  DeviceCell,
  TextLink,
  BranchCell,
  DateTimeCell,
  SignalCell,
  BatteryCell,
  MeterCell
} from './DeviceCells';
import { apiGetDevicesInformation } from '@services/ManageDevices/DeviceServices';
import { apiGetFavoriteFilters } from '@services/ManageCustomers/FavoriteFilterServices';
import { showError } from '@components/notification';
import { communityList, downloadBlobFile, formatCurrency } from '@utils/utils';
import {
  SearchOutlined,
  ClearOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import { Link } from 'react-router-dom';
import TabPane from 'antd/es/tabs/TabPane';
import { URL_API } from '@configs/index';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import WarningImage from '@assets/images/warning.png';
import OnOffImage from '@assets/images/on-off.png';
import NbIOTImage from '@assets/images/nb-iot.png';
import Loading from '@components/Loading';
import { usePermission } from '@contexts/Auth/UsePermission';
import FilterAdvanceDrawer from '@components/FilterAdvance';

const { Option } = Select;
const { Title, Paragraph } = Typography;
const SIGNAL_TABS = ['RSRP_STRONG', 'RSRP_NORMAL', 'RSRP_LOW'];

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('HP');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [countDetail, setCountDetail] = useState(null);
  const [custId, setCustId] = useState(null);
  const [status, setStatus] = useState('all');
  const [filterWarning, setFilterWarning] = useState(0);
  const [filterRSRP, setFilterRSRP] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const { dashboard } = usePermission();
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});
  const [favoriteFilters, setFavoriteFilters] = useState([]);
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState(undefined);
  const [loadingFavoriteFilters, setLoadingFavoriteFilters] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => {
    setIsOpen(true);
  };

  const onDrawerClose = () => {
    setIsOpen(false);
  };

  const fetchFavoriteFilters = async () => {
    try {
      setLoadingFavoriteFilters(true);
      const res = await apiGetFavoriteFilters();
      const customerFilters = (res.data?.data || []).filter((f) => f.filter_type === 'customer');
      setFavoriteFilters(
        customerFilters.map((f) => ({
          label: `${f.filter_name} (${f.item_ids?.length || 0} KH)`,
          value: f.id,
          item_ids: f.item_ids
        }))
      );
    } catch (err) {
      console.error('Error fetching favorite filters:', err);
    } finally {
      setLoadingFavoriteFilters(false);
    }
  };

  const fetchData = async () => {
    try {
      // setIsFetching(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      const res = await apiGetDevicesInformation({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() ?? undefined,
        customerId: custId ?? undefined,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        status: ['online', 'offline']?.includes(status) ? status : undefined,
        warning: ['warning']?.includes(status) ? true : undefined,
        rsrp: SIGNAL_TABS.includes(status) ? status : undefined,
        listCust,
        ...dataFilterAdvance
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
      setCountDetail(res.data?.meta?.countDetail);
    } catch (err) {
      showError('Không lấy được dữ liệu!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 20000);

    return () => clearInterval(interval);
  }, [search, selectedCommunity, currentPage, custId, pageSize, status, dataFilterAdvance]);

  useEffect(() => {
    fetchFavoriteFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status]);

  useEffect(() => {
    const handleFilterEvent = (event) => {
      const { status } = event.detail;
      setStatus(status);
      setCurrentPage(1);
    };

    window.addEventListener('filterDeviceStatus', handleFilterEvent);
    return () => window.removeEventListener('filterDeviceStatus', handleFilterEvent);
  }, []);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const WarningCell = ({ record }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const info = JSON.parse(record['/82/0']);
    const magneticAttackStatus = info?.magneticAttackStatus; // Cảnh báo đang bị tấn công từ tính
    const antiDemolitionStatus = info?.antiDemolition; // Cảnh báo đang bị tấn công tháo rời
    const happenedMagneticAttackStatus = info?.happenedMagneticAttack; // Cảnh báo đã bị tấn công từ tính
    const happenedAntiDemolitionStatus = info?.happenedAntiDemolition; // Cảnh báo đã bị tấn công tháo rời
    // const overFlowStatus = info?.overFlowStatus; // Cảnh báo dòng chảy đang vượt ngưỡng
    // const stopStatus = info?.stopStatus; // Cảnh báo kẹt công tơ
    const denseDataDiffirenceOfTheFlowPulseNumber =
      record?.denseData_diffirenceOfTheFlowPulseNumber;
    const reverseFlows = denseDataDiffirenceOfTheFlowPulseNumber?.filter(
      (item) => item < 0
    )?.length; // Cảnh báo dòng chảy ngược

    const showModal = () => {
      setIsModalVisible(true);
    };

    const handleOk = () => {
      setIsModalVisible(false);
    };

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const highlightStyle = (checked) => ({
      fontWeight: checked ? 'bold' : 'normal'
    });

    return (
      <>
        {antiDemolitionStatus ||
        happenedAntiDemolitionStatus ||
        magneticAttackStatus ||
        happenedMagneticAttackStatus ||
        reverseFlows ? (
          <>
            <div style={{ color: '#e11d48', fontWeight: 'bold' }}>
              <ExclamationCircleOutlined /> Có cảnh báo{' '}
              <Button type="link" onClick={showModal}>
                Xem chi tiết
              </Button>
            </div>
            <Modal
              title="Chi tiết cảnh báo"
              visible={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}>
              <Checkbox checked={magneticAttackStatus} style={highlightStyle(magneticAttackStatus)}>
                Cảnh báo đang bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedMagneticAttackStatus}
                style={highlightStyle(happenedMagneticAttackStatus)}>
                Cảnh báo đã bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox checked={antiDemolitionStatus} style={highlightStyle(antiDemolitionStatus)}>
                Cảnh báo đang bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedAntiDemolitionStatus}
                style={highlightStyle(happenedAntiDemolitionStatus)}>
                Cảnh báo đã bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox checked={reverseFlows} style={highlightStyle(reverseFlows)}>
                Cảnh báo dòng chảy ngược
              </Checkbox>
            </Modal>
          </>
        ) : (
          <div>-</div>
        )}
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 60,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: '/84/0',
        render: (text, record) => {
          const info = JSON.parse(record['/84/0']);
          const isOnline =
            !!info &&
            (Number(Date.now()) - Number(record.last_seen_at)) / 1000 <=
              Number(info?.reportPeriod) + 60;
          return <StatusPill online={isOnline} />;
        },
        align: 'center',
        width: 100
      },
      {
        title: 'Thiết bị',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 190,
        render: (text, record) => (
          <DeviceCell
            to={`/device/detail/${record.id}`}
            serial={record.serial_no}
            model={record.model}
          />
        )
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        align: 'center',
        width: 110,
        render: (text, record) => (
          <TextLink to={`/device/detail/${record.id}`} mono>
            {record.customer_id}
          </TextLink>
        )
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 220,
        render: (text, record) => (
          <TextLink to={`/device/detail/${record.id}`} clamp>
            {record.customer_name}
          </TextLink>
        )
      },
      {
        title: 'CN',
        dataIndex: 'branch',
        width: 130,
        render: (value) => <BranchCell name={value} />
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        align: 'center',
        width: 100
      },
      {
        title: 'DHK',
        dataIndex: 'parent_device',
        align: 'center',
        width: 100
      },
      {
        title: 'Tuyến',
        dataIndex: 'route',
        align: 'center',
        width: 100
      },
      {
        title: 'Cỡ ĐH',
        dataIndex: 'size',
        align: 'center',
        width: 100
      },
      {
        title: 'Lần truyền cuối',
        dataIndex: 'last_seen_at',
        align: 'center',
        width: 120,
        render: (text, record) => <DateTimeCell value={record.last_seen_at} />
      },
      {
        title: (
          <Tooltip title="Cường độ sóng (dBm)">
            <span> Cường độ sóng (dBm)</span>
          </Tooltip>
        ),
        dataIndex: '/99/0',
        align: 'center',
        render: (text, record) => {
          const info = JSON.parse(record['/99/0']);
          return <SignalCell rsrp={info ? info.rsrp / 10 : null} />;
        },
        width: 100
      },
      // {
      //   title: (
      //     <Tooltip title="Tỷ lệ tín/tạp (dB)">
      //       <span
      //         style={{
      //           whiteSpace: 'nowrap',
      //           overflow: 'hidden',
      //           textOverflow: 'ellipsis',
      //           maxWidth: 100,
      //           display: 'inline-block'
      //         }}>
      //         Tỷ lệ tín/tạp (dB)
      //       </span>
      //     </Tooltip>
      //   ),
      //   dataIndex: '/99/0',
      //   render: (text, record) => {
      //     let info = JSON.parse(record['/99/0']);
      //     if (!info) return <div>-</div>;
      //     let snr = (info.snr / 10).toFixed(2);
      //     return <div>{snr}</div>;
      //   },
      //   width: 100
      // },
      {
        title: 'Pin (%)',
        dataIndex: 'voltage',
        align: 'center',
        render: (text, record) => {
          if (!record.voltage) return <div>-</div>;

          /* Công thức cũ:
          const installTime = new Date(record.created_at).getTime();
          const lastSeenAt = record.last_seen_at;
          const timeDifference = lastSeenAt - installTime;
          let remainingBatCap =
            ((8500 * 1000 -
              (timeDifference * 30) / (3600 * 1000) -
              record.number_of_updates * 1394.52) /
              (8500 * 1000)) *
            100;
          if (isNaN(remainingBatCap)) remainingBatCap = 0;
          remainingBatCap = Math.max(0, Math.min(100, remainingBatCap));
          */

          const initialBattery = 8500 * 1000;
          const startedAt = new Date(record.install_time || record.created_at).getTime();
          const lastSeenAt = Number(record.last_seen_at);
          const operatingTimeMs =
            Number.isFinite(startedAt) && Number.isFinite(lastSeenAt)
              ? Math.max(0, lastSeenAt - startedAt)
              : 0;
          const numberOfUpdates = Math.max(0, Number(record.number_of_updates) || 0);
          const timeConsumption = (operatingTimeMs * 30) / (3600 * 1000);
          const transmissionConsumption = numberOfUpdates * 1394.52;
          const remainingBattery = initialBattery - timeConsumption - transmissionConsumption;
          const remainingBatCap = Math.max(
            0,
            Math.min(100, (remainingBattery / initialBattery) * 100)
          );

          return <BatteryCell percent={remainingBatCap} />;
        },
        width: 100
      },
      {
        title: (
          <Tooltip title="Chỉ số đồng hồ (m3)">
            <span> Chỉ số đồng hồ (m3)</span>
          </Tooltip>
        ),
        dataIndex: 'meter_reading',
        align: 'right',
        render: (text, record) => <MeterCell value={record.meter_reading / 1000.0} />,
        width: 130
      }
      // {
      //   title: (
      //     <Tooltip title="Trạng thái cảnh báo">
      //       <span> Trạng thái cảnh báo </span>
      //     </Tooltip>
      //   ),
      //   dataIndex: '/82/0',
      //   align: 'center',
      //   render: (text, record) => <WarningCell record={record} />,
      //   width: 120
      // }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleCustIdChange = (e) => {
    setCustId(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      await downloadBlobFile(
        `${URL_API}/api/devices/export-dashboard-report`,
        'Thống kê chung.xlsx',
        {
          keyWord: search?.trim() ?? undefined,
          customerId: custId ?? undefined,
          community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
          status: ['online', 'offline'].includes(status) ? status : undefined,
          warning: status === 'warning' || filterWarning == 1 ? 1 : undefined,
          rsrp: SIGNAL_TABS.includes(status) ? status : filterRSRP || undefined,
          listCust,
          ...dataFilterAdvance
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSearch('');
    setCustId('');
    setCurrentPage(1);
    setStatus('all');
    setFilterWarning(0);
    setFilterRSRP('');
    setDataFilterAdvance({});
    setSelectedFavoriteFilter(undefined);
    setFlagSearch(!flagSearch);
  };

  return (
    <div className="dashboard" id="device-list-table">
      <Card className="list-filter-card" bodyStyle={{ padding: '12px' }}>
        <Row gutter={[8, 8]}>
          <Col xs={4} sm={3} md={2} lg={1} xl={1}>
            <Button
              icon={<FilterOutlined />}
              onClick={openDrawer}
              title="Bộ lọc nâng cao"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={20} sm={7} md={6} lg={5} xl={5}>
            <Select
              placeholder="Chọn Bộ lọc theo dõi..."
              value={selectedFavoriteFilter}
              onChange={(value) => {
                setSelectedFavoriteFilter(value);
                setCurrentPage(1);
              }}
              loading={loadingFavoriteFilters}
              allowClear
              options={favoriteFilters}
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={24} sm={7} md={6} lg={5} xl={5}>
            <Input
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm số serial, imei..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={24} sm={7} md={6} lg={5} xl={5}>
            <Input
              value={custId}
              onChange={handleCustIdChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm tên, mã KH..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={8} sm={5} md={3} lg={3} xl={3}>
            <Button
              type="primary"
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{ width: '100%' }}>
              Tìm kiếm
            </Button>
          </Col>
          <Col xs={8} sm={5} md={3} lg={3} xl={3}>
            <Button
              type="text"
              className="list-clear-btn"
              style={{ width: '100%' }}
              icon={<ClearOutlined />}
              onClick={clearFilter}>
              Xoá bộ lọc
            </Button>
          </Col>
          {dashboard.export && (
            <Col xs={8} sm={6} md={2} lg={2} xl={2}>
              <Button
                color="primary"
                variant="filled"
                loading={loading}
                onClick={handleExportExcelButtonClick}
                icon={<DownloadOutlined />}
                style={{ width: '100%' }}>
                Xuất Excel
              </Button>
            </Col>
          )}
        </Row>
      </Card>
      {/* <Card> */}
      <Card className="list-table-card" bodyStyle={{ padding: '0px' }}>
        <Tabs
          activeKey={status}
          defaultActiveKey="all"
          onChange={(value) => {
            setCurrentPage(1);
            setStatus(value);
          }}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị Online`} key={'online'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị rớt mạng`} key={'offline'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          {/* <TabPane tab={`Thiết bị có cảnh báo`} key={'warning'}>
          <Table
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane> */}
          <TabPane tab={`Thiết bị sóng tốt`} key={'RSRP_STRONG'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị sóng trung bình`} key={'RSRP_NORMAL'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị sóng yếu`} key={'RSRP_LOW'}>
            <Table
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
        </Tabs>
      </Card>
      {/* </Card> */}
      <FilterAdvanceDrawer
        isOpen={isOpen}
        onDrawerClose={onDrawerClose}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
}
